import _ from 'lodash';
import { api } from '_/services/api';
import { store } from '_/vuex/store';

// sessionStorage key for a toast that must survive a full page load (M2-121).
const FLASH_KEY = 'm2.cp.flash';
const FLASH_TTL_MS = 30000;
// How long this slot's toasts stay (core's default is 3 s).
const TOAST_DELAY_MS = 10000;

/**
 * M2: two things rendered next to the entry form's info panel on the native /cp screens.
 *
 * 1. Commissions → New Entry (scope §5.3): a live commission preview. The admin fills only
 *    Referral + Agency Commission (the derived fields are hidden); this reads those from the
 *    admin vuex store and shows rate / partner amount / sponsor bonus via
 *    GET /admin/commissions/preview (the same calc CommissionsModuleExtensions runs on save).
 *
 * 2. Referrals → Edit: a "Remove referral" action. Removal must capture a mandatory reason
 *    (audited) and is refused if the referral has commissions, so the native "Delete Entry"
 *    is blocked (ReferralsModuleExtensions::interruptDelete) and this button is the only path.
 *    It prompts for a reason, calls DELETE /api/admin/referrals/{id}, then redirects to the list
 *    and confirms; a refusal (409 REFERRAL_HAS_COMMISSIONS) is shown as a lasting toast.
 *
 * Every other module/screen leaves the slot empty.
 */
export default {
    name: 'AdminSlotAboveInfoPanel',

    data: () => ({
        preview: null,
        error: null,
        removing: false,
        duplicates: null,
    }),

    computed: {
        isCommissionsCreate() {
            const admin = this.$store.state.admin;
            return admin.selectedModule.table_name === 'commissions'
                && admin.editorMode === 'create';
        },

        agencyCommission() {
            return _.get(this.$store.state.admin.editedEntry, 'fields.agency_commission');
        },

        referralId() {
            // Create-mode shape set by the Select2 relation field.
            return _.get(this.$store.state.admin.editedEntry, 'fields.referral.system.id');
        },

        isReferralsEdit() {
            const admin = this.$store.state.admin;
            return admin.selectedModule.table_name === 'referrals'
                && admin.editorMode === 'edit';
        },

        editedReferralId() {
            return _.get(this.$store.state.admin.editedEntry, 'system.id');
        },
    },

    mounted() {
        this.showFlash();
    },

    watch: {
        agencyCommission() { this.fetchPreview(); },
        referralId() { this.fetchPreview(); },
        isCommissionsCreate() { this.fetchPreview(); },
        // immediate: the entry is usually already loaded when this slot mounts, so the
        // id doesn't "change" — fire on mount too, not only on later navigation.
        editedReferralId: { handler() { this.fetchDuplicates(); }, immediate: true },
    },

    methods: {
        fetchPreview() {
            this.preview = null;
            this.error = null;

            if (!this.isCommissionsCreate || !this.referralId || !this.agencyCommission || this.agencyCommission < 750) {
                return;
            }

            api.get('admin/commissions/preview?referral_id=' + this.referralId + '&agency_commission=' + this.agencyCommission)
                .then((response) => { this.preview = response.data.body; })
                .catch((err) => { this.error = _.get(err, 'data.message', 'Greška pri izračunu.'); });
        },

        /**
         * Show a /cp toast. Core's defaults (3 s, semi-transparent, fades away on hover) make
         * an outcome easy to miss (M2-121), so these stay 10 s and can be read with the mouse
         * over them.
         */
        notify(subject, message, type) {
            store.dispatch('notification/addNotification', {
                notification: { subject, compiled_message: message, type, delay: TOAST_DELAY_MS, nonblock: false },
            });
        },

        fetchDuplicates() {
            this.duplicates = null;

            if (!this.isReferralsEdit || !this.editedReferralId) {
                return;
            }

            // Live recompute (scope §6.2) — the stored score reflects submission time.
            api.get('admin/referrals/' + this.editedReferralId + '/duplicates')
                .then((response) => { this.duplicates = response.data.body; })
                .catch(() => { this.duplicates = null; });
        },

        goToReferral(id) {
            // Base-relative so the /cp base isn't doubled.
            this.$router.push('/admin/referrals/' + id);
        },

        removeReferral() {
            if (this.removing || !this.editedReferralId) {
                return;
            }

            // Removal is irreversible and needs a reason (audited server-side). The native
            // "Delete Entry" is blocked, so this prompt is the only way to remove a referral.
            const reason = window.prompt('Razlog uklanjanja preporuke (obavezno). Ne upisujte ime, telefon ni email klijenta:');
            if (reason === null) {
                return; // cancelled
            }
            if (!reason.trim()) {
                this.notify('Greška', 'Razlog je obavezan.', 'error');
                return;
            }

            this.removing = true;
            // The saved name (anchor text), not the live form field, which may hold an
            // unsaved edit. Partner-entered, and core's toast renders its text as HTML, so it
            // is escaped; a translatable {en: …} value is not a name and is left out.
            const entry = this.$store.state.admin.editedEntry;
            const rawName = _.get(entry, 'system.anchor_text') || _.get(entry, 'fields.client_name');
            const referralName = _.isString(rawName) ? _.escape(rawName.trim()) : '';
            // Resolved up front (and through the router, so the /cp base is included): a
            // failure after the DELETE succeeded must not be reported as "not removed".
            const listHref = this.$router.resolve('/admin/referrals').href;

            api.delete('admin/referrals/' + this.editedReferralId + '?reason=' + encodeURIComponent(reason))
                .then(
                    () => {
                        // A full load of the list route, not a router push: the entry list on
                        // the left keeps the removed referral otherwise, and rebuilding it in
                        // place races the route change and lists entries twice. The
                        // confirmation rides along in sessionStorage and is shown by this slot
                        // after the reload. The list route opens core's empty "Creating a New
                        // Entry" form, so the message says what that form is.
                        this.flash(
                            'Preporuka uklonjena',
                            'Preporuka ' + (referralName ? referralName + ' ' : '') + 'je trajno uklonjena. '
                                + 'Forma koja se otvorila je prazna forma za novu preporuku — ne morate je popunjavati.',
                            'success'
                        );
                        window.location.assign(listHref);
                    },
                    // A rejection handler, not .catch(): only a failed DELETE may say nothing
                    // was removed.
                    (err) => {
                        const code = _.get(err, 'data.message') || _.get(err, 'body.message');
                        const message = code === 'REFERRAL_HAS_COMMISSIONS'
                            ? 'Preporuka ima provizije, pa ne može da se ukloni. Ništa nije promenjeno.'
                            : 'Uklanjanje nije uspelo (' + _.escape(code || 'nepoznata greška') + '). Ništa nije promenjeno.';
                        this.notify('Preporuka nije uklonjena', message, 'error');
                        this.removing = false;
                    }
                );
        },

        /** Queue a toast for the next page load (see showFlash). */
        flash(subject, message, type) {
            try {
                sessionStorage.setItem(FLASH_KEY, JSON.stringify({ subject, message, type, at: Date.now() }));
            } catch (e) {
                // Storage unavailable: the reload still shows the updated list.
            }
        },

        /**
         * Show a toast queued before a reload. The first slot instance that mounts consumes
         * it; one is enough.
         */
        showFlash() {
            let flash = null;
            try {
                flash = JSON.parse(sessionStorage.getItem(FLASH_KEY) || 'null');
                sessionStorage.removeItem(FLASH_KEY);
            } catch (e) {
                return;
            }
            // Only right after the reload that queued it: a message left behind (the slot did
            // not mount on the target page) must not pop up later on an unrelated form.
            if (flash && Date.now() - (flash.at || 0) < FLASH_TTL_MS) {
                this.notify(flash.subject, flash.message, flash.type);
            }
        },
    },
};
